import type { FC } from 'react';
import { Button } from '@selfinspectionorg/ui-kit';
import { MfaMethod } from '@shared/services';
import styles from './Mfa.module.scss';
import { useMfaVerify } from './useMfaVerify';
import { usePasskeyCeremony } from './usePasskeyCeremony';
import { CodeVerify, EmailStep, MethodPicker, PasskeyButton } from './components';
import { Card } from '@/components/ui';
import { type MfaVerifyResponse } from '@/shared/api/auth';

interface Props {
  mfaPending: string;
  mfaMethods: MfaMethod[];
  /**
   * A verified factor yields the raw dual-outcome result: a session (account-scoped `mfaPending`, the
   * login/switch fallback) or an `{ outcome: 'accounts', accountsProof }` (identity-scoped `mfaPending`,
   * the MFA-first login path). The container discriminates — adopt the session, or capture the proof.
   */
  onSuccess: (view: MfaVerifyResponse) => void;
  /** Invoked from the expired card. Falls back to `onBack` when omitted. */
  onExpired?: () => void;
  /** Sole-method Back / ceremony exit. With multiple methods, Back returns to the picker instead. */
  onBack?: () => void;
  /** Copy for the expired card; defaults suit a login-origin ceremony. */
  expired?: { description?: string; actionLabel?: string };
}

const noop = () => {};

/**
 * Inline MFA verification, rendered in place on `/login` and `/switch` (never its own route). Reuses
 * the shared step components in verify mode. Success never adopts here — it hands the session up via
 * `onSuccess`. A single advertised method skips the picker; two or more start on `select`.
 */
export const MfaVerify: FC<Props> = ({ mfaPending, mfaMethods, onSuccess, onExpired, onBack, expired }) => {
  const flow = useMfaVerify(mfaMethods);
  const passkey = usePasskeyCeremony({ mfaPending, mode: 'verify', onSuccess, onExpired: flow.expire });

  const exit = onBack ?? noop;

  // Selecting passkey fires the ceremony in place — no passkey step. TOTP/email advance to their
  // form on the `verify` step as before.
  const handleSelect = (method: MfaMethod) => {
    if (method === MfaMethod.PASSKEY) {
      void passkey.run();

      return;
    }

    flow.selectMethod(method);
  };

  if (flow.step === 'expired') {
    return (
      <Card className={styles.wrapper}>
        <Card.Title
          title="Session expired"
          description={expired?.description ?? 'Your two-factor session has expired. Please sign in again to continue.'}
        />
        <Button width="100%" onClick={onExpired ?? exit} data-testid="mfa-back-to-login-button">
          {expired?.actionLabel ?? 'Back to Login'}
        </Button>
      </Card>
    );
  }

  if (flow.step === 'select') {
    // A passkey ceremony fired from the picker only surfaces UI when it fails: the retry screen,
    // whose Back clears the error and returns to the picker.
    if (passkey.error !== null) {
      return (
        <Card className={styles.wrapper}>
          <PasskeyButton
            mode="verify"
            isRunning={passkey.isRunning}
            error={passkey.error}
            onRun={passkey.run}
            onBack={passkey.reset}
          />
        </Card>
      );
    }

    return (
      <Card className={styles.wrapper}>
        {/* Back leaves the picker entirely — the same reset every ceremony exit performs. */}
        <Card.BackButton onClick={exit} testId="mfa-back-button" />
        <MethodPicker methods={mfaMethods} mode="verify" busy={passkey.isRunning} onSelect={handleSelect} />
      </Card>
    );
  }

  // step === 'verify': render the chosen method's verification component. With multiple methods, Back
  // returns to the picker; when a single method skipped the picker, Back leaves the ceremony. Passkey
  // only lands here as a sole method (from the picker it runs in place, above) — an explicit-click
  // screen, since a fresh landing has no gesture to auto-fire from.
  const shared = {
    mfaPending,
    onExpired: flow.expire,
    onBack: flow.canReturnToPicker ? flow.backToPicker : exit,
  };

  let content = null;

  if (flow.method === MfaMethod.PASSKEY) {
    content = (
      <PasskeyButton
        mode="verify"
        isRunning={passkey.isRunning}
        error={passkey.error}
        onRun={passkey.run}
        onBack={exit}
      />
    );
  } else if (flow.method === MfaMethod.EMAIL) {
    content = <EmailStep {...shared} mode="verify" onSuccess={onSuccess} />;
  } else if (flow.method === MfaMethod.TOTP) {
    content = <CodeVerify {...shared} onSuccess={onSuccess} />;
  }

  return <Card className={styles.wrapper}>{content}</Card>;
};
